import Loading from '@ingka/loading';
import { Navigate, Route, Routes } from 'react-router';
import AppShell from '@/components/layout/AppShell';
import ClassificationStep from '@/components/steps/ClassificationStep';
import EntityStep from '@/components/steps/EntityStep';
import ProductStep from '@/components/steps/ProductStep';
import SuccessStep from '@/components/steps/SuccessStep';
import UploadStep from '@/components/steps/UploadStep';
import { AppProvider, useApp } from '@/context/AppContext';
import AccessDeniedPage from '@/pages/AccessDeniedPage';
import AdminClassificationsPage from '@/pages/AdminClassificationsPage';
import AdminLayout from '@/pages/AdminLayout';
import AdminProductsPage from '@/pages/AdminProductsPage';
import AdminPage from '@/pages/AdminUsersPage';
import AdminNotificationsPage from '@/pages/AdminNotificationsPage';
import ArchivePage from '@/pages/ArchivePage';
import DataCatalogPage from '@/pages/DataCatalogPage';
import LandingPage from '@/pages/LandingPage';
import LocationsPage from '@/pages/LocationsPage';
import LocationsSuccessPage from '@/pages/LocationsSuccessPage';
import WaitingForAccessPage from '@/pages/WaitingForAccessPage';

function LoadingScreen() {
  return (
    <div
      style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Loading text="Checking sign-in..." />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useApp();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireScope({ children, scope }: { children: React.ReactNode; scope?: string }) {
  const { user, isLoading } = useApp();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;

  // Admin bypasses all scope checks
  if (user.role === 'admin') return <>{children}</>;

  // Wildcard scope in user's scope list = access to everything
  if (user.product_scope?.includes('*')) return <>{children}</>;

  // If a specific scope is required, check for it
  if (scope) {
    if (!user.product_scope?.includes(scope)) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  // Fallback: user must have ANY scope
  if (!user.product_scope || user.product_scope.length === 0) {
    return <Navigate to="/waiting-for-access" replace />;
  }

  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useApp();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RootRoute() {
  const { user, isLoading, catalog } = useApp();

  if (isLoading) return <LoadingScreen />;
  if (!user) return <LandingPage />;

  if (user.role !== 'admin' && (!user.product_scope || user.product_scope.length === 0)) {
    return <Navigate to="/waiting-for-access" replace />;
  }

  // Derive routing from the catalog: users with only special-route products
  // land on that product's dedicated page. Users with any wizard-eligible
  // product go to the generic upload wizard.
  const products = catalog?.products ?? [];
  const wizardProducts = products.filter((p) => !p.special_route);
  const specialRouteProducts = products.filter((p) => p.special_route);

  if (
    user.role !== 'admin' &&
    wizardProducts.length === 0 &&
    specialRouteProducts.length > 0
  ) {
    const target = specialRouteProducts[0]?.special_route;
    if (target) return <Navigate to={target} replace />;
  }

  return <Navigate to="/upload/product" replace />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />

      <Route
        path="/waiting-for-access"
        element={
          <RequireAuth>
            <WaitingForAccessPage />
          </RequireAuth>
        }
      />

      <Route
        path="/archive"
        element={
          <RequireScope>
            <ArchivePage />
          </RequireScope>
        }
      />
      <Route
        path="/locations"
        element={
          <RequireScope scope="locations">
            <LocationsPage />
          </RequireScope>
        }
      />
      <Route
        path="/locations/success"
        element={
          <RequireScope scope="locations">
            <LocationsSuccessPage />
          </RequireScope>
        }
      />

      <Route
        path="/data-catalog"
        element={
          <RequireAuth>
            <DataCatalogPage />
          </RequireAuth>
        }
      />

      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }
      >
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<AdminPage />} />
        <Route path="catalog/products" element={<AdminProductsPage />} />
        <Route path="catalog/classifications" element={<AdminClassificationsPage />} />
        <Route path="notifications" element={<AdminNotificationsPage />} />
      </Route>

      <Route path="/upload" element={<Navigate to="/upload/product" replace />} />
      <Route
        path="/upload/product"
        element={
          <RequireScope>
            <AppShell>
              <ProductStep />
            </AppShell>
          </RequireScope>
        }
      />
      <Route
        path="/upload/entity"
        element={
          <RequireScope>
            <AppShell>
              <EntityStep />
            </AppShell>
          </RequireScope>
        }
      />
      <Route
        path="/upload/classification"
        element={
          <RequireScope>
            <AppShell>
              <ClassificationStep />
            </AppShell>
          </RequireScope>
        }
      />
      <Route
        path="/upload/file"
        element={
          <RequireScope>
            <AppShell>
              <UploadStep />
            </AppShell>
          </RequireScope>
        }
      />
      <Route
        path="/upload/success"
        element={
          <RequireScope>
            <AppShell>
              <SuccessStep />
            </AppShell>
          </RequireScope>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppRouter />
    </AppProvider>
  );
}
